// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Fragment } from "react";
import { css } from "styled-components";

interface Props {
    categories: AutocompleteItemModel[] | null;
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteCategoriesStyles;
}

export interface AutocompleteCategoriesStyles {
    headerText?: TypographyPresentationProps;
    categoryItemText?: TypographyPresentationProps;
    categoryItemTextInner?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    wrapper?: InjectableCss;
    leftColumn?: InjectableCss;
    rightColumn?: InjectableCss;
}

export const autocompleteCategoriesStyles: AutocompleteCategoriesStyles = {
    headerText: {
        size: fontConfig.fontSize.small,
        weight: fontConfig.weight.semiBold,
        lineHeight: "17px",
        color: "text.main",
        transform: "uppercase",
        css: css`
            letter-spacing: 1px;
            text-align: left;
        `,
    },
    link: {
        typographyProps: {
            size: fontConfig.fontSize.small,
            weight: fontConfig.weight.light,
            lineHeight: "20px",
            color: "common.ui1",
            css: css`
                letter-spacing: 0.2px;
                display: block;
                width: 100%;
                text-align: left;
                &&& {
                    color: ${getColor("text.ui1")};
                    white-space: pre-wrap;
                }
                b {
                    color: ${getColor("text.main")};
                }
            `,
            ellipsis: true,
        },
    },
    wrapper: {
        css: css`
            margin-bottom: 40px;
            display: flex;
            flex-direction: column;
        `,
    },
    leftColumn: {
        css: css`
            width: 78px;
            margin-right: 35px;
            margin-bottom: 12px;
        `,
    },
    rightColumn: {
        css: css`
            width: calc(100% - 113px);
        `,
    },
    categoryItemText: {
        size: fontConfig.fontSize.small,
        weight: fontConfig.weight.semiBold,
        lineHeight: "20px",
        color: "common.ui1",
        css: css`
            letter-spacing: 0.2px;
            display: block;
            width: 100%;
            text-align: left;
        `,
    },
    categoryItemTextInner: {
        size: fontConfig.fontSize.regular,
        weight: fontConfig.weight.light,
        lineHeight: "20px",
        color: "common.ui1",
        css: css`
            letter-spacing: 0.2px;
            text-align: left;

            &:hover,
            &:focus {
                text-decoration: underline;
                cursor: pointer;
            }
        `,
    },
};

const styles = autocompleteCategoriesStyles;

class AutocompleteCategories extends React.Component<Props> {
    private readonly styles: AutocompleteCategoriesStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { categories } = this.props;
        if (!categories || categories.length === 0) {
            return null;
        }

        const styles = this.styles;
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.leftColumn}>
                    <Typography
                        {...styles.headerText}
                        data-test-selector="autocompleteCategories_Header"
                        className="autocomplete-categories-header"
                    >
                        {translate("Categories")}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.rightColumn}>
                    <GridContainer gap={5}>
                        {categories.map((category, index) => (
                            <GridItem
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                width={12}
                            >
                                {category.subtitle && (
                                    <Typography
                                        {...styles.categoryItemText}
                                        className="autocomplete-categories-item-text"
                                    >
                                        <Link
                                            {...styles.link}
                                            className="autocomplete-categories-item-link"
                                            onClick={() => {
                                                this.props.goToUrl(category.url);
                                            }}
                                        >
                                            {category.displayTitle}
                                        </Link>
                                        <Typography
                                            as="span"
                                            {...styles.categoryItemTextInner}
                                            onClick={() => {
                                                this.props.goToUrl(category.properties.subTitleUrl);
                                            }}
                                        >{` ${translate("in")} ${category.subtitle}`}</Typography>
                                    </Typography>
                                )}
                                {!category.subtitle && (
                                    <Link
                                        {...styles.link}
                                        onClick={() => {
                                            this.props.goToUrl(category.url);
                                        }}
                                        className="autocomplete-categories-link"
                                    >
                                        {category.displayTitle}
                                    </Link>
                                )}
                            </GridItem>
                        ))}
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

export default AutocompleteCategories;
